import { useCallback, useMemo, useState } from 'react';
import type { StyleProp, TextStyle, ViewStyle } from 'react-native';
import { PixelRatio, StyleSheet } from 'react-native';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { getTokenValue, isAndroid, isWeb, useProps } from '@tamagui/core';

import { ChevronDown, ChevronUp } from '../../../__generated__/icons';
import { AnalyticsProps } from '../../../helpers/analytics';
import { getFontName, getTypographyValue } from '../../../helpers/fontTokens';
import { isKeyOfObject } from '../../../helpers/utils';
import { TrackableContainer } from '../../shared/TrackableContainer';
import { useInputId, useInputState } from '../shared/hooks';
import { InputError } from '../shared/InputError';
import { InputFrame } from '../shared/InputFrame';
import { InputLabel } from '../shared/InputLabel';
import type { InputProps, InputStateProp } from '../shared/InputProps';
import { PickerSelect } from './PickerSelect';
import type { PickerSelectProps } from './PickerSelect';

const { textInput: textInputTokens } = tokens;

export type DropdownProps = Partial<AnalyticsProps> &
  InputProps &
  Omit<
    PickerSelectProps,
    | 'pickerProps'
    | 'useNativeAndroidPickerStyle'
    | 'fixAndroidTouchableBug'
    | 'style'
    | 'touchableWrapperProps'
    | 'Icon'
    | 'items'
    | 'placeholder'
    | 'value'
    | 'onValueChange'
  > &
  Pick<NonNullable<PickerSelectProps['pickerProps']>, 'onFocus' | 'onBlur'> & {
    /**
     * Callback called when the selected value changes
     */
    onValueChange?: (value: string) => void;
    /**
     * Options passed to the dropdown
     */
    options: Array<{
      /**
       * The text displayed to the user
       */
      label: string;
      /**
       * The value used internally. Make sure these are all unique strings
       */
      value: string;
    }>;
    /**
     * Placeholder displayed when no option has been selected. Defaults to a
     * label of `"Select an item"` and a value of `""`
     */
    placeholderOption?: {
      /**
       * The text displayed to the user
       */
      label: string;
      /**
       * The value used internally. Defaults to an empty string
       */
      value?: string;
    };
    /**
     * The prefilled value for the dropdown
     */
    prefilledValue?: string;
    /**
     * Callback called when the validation state of the dropdown changes. The
     * validation state is 'unknown' when the dropdown is first rendered and
     * updated to either 'valid' or 'invalid' when the user blurs the dropdown
     * or selects a new value.
     */
    onValidationChange?: (validationState: 'valid' | 'invalid' | 'unknown') => void;
    /**
     * Custom error messages for input - otherwise the default will be used
     */
    customErrorMessages?: {
      /**
       * Custom error message for when the field is required
       * Default: 'This field is required'
       */
      fieldRequired?: string;
    };
  };

/**
 * The Dropdown component lets the user select a single option from a menu. It
 * renders the native `<select>` element on web and a React Native component that
 * emulates native pickers on Android and iOS. (Under the hood it uses the
 * [react-native-picker-select](https://github.com/lawnstarter/react-native-picker-select)
 * library.)
 *
 * The placeholder text defaults to "Select an item". Pass
 * `placeholderOption.label` to customize the placeholder text. If needed, you
 * can also customize the value of the placeholder by passing
 * `placeholderOption.value` (it defaults to an empty string).
 *
 * Pass `required` to make the dropdown a required field. When the dropdown is
 * required, it will show an error message if the user selects the placeholder
 * option or blurs the select element without choosing an option. The error
 * message defaults to "This field is required" but you can customize it by
 * passing `customErrorMessages.fieldRequired`.
 *
 * If you want an initial value to appear in the dropdown, you can pass it as a `prefilledValue` prop. This should be a value that does not change after the initial render.
 *
 * Pass `onValidationChange` to be notified when the dropdown's validation
 * state changes. This is useful for disabling submit buttons while the dropdown
 * is in an invalid state. (See also: the ValidatedTextInput docs)
 *
 * If you want to track impression events ("inputViewEvent"), pass
 * `analyticsName` and `trackImpression`.
 */
export const Dropdown = ({
  analyticsName,
  trackImpression,
  title,
  supportText,
  required,
  errorText: externalErrorText,
  invalid: externalInvalid,
  disabled,
  id,
  name,
  onFocus,
  onBlur: externalOnBlur,
  onOpen,
  onClose,
  onValueChange,
  options,
  placeholderOption,
  prefilledValue,
  customErrorMessages,
  onValidationChange,
  ...propsIn
}: DropdownProps) => {
  const { size = 'medium', ...props } = useProps(propsIn);

  const pickerId = useInputId(id);

  const items = useMemo(() => {
    return options.map(({ label, value }) => ({ label, value, key: value }));
  }, [options]);

  const placeholder = useMemo(() => {
    return {
      label: placeholderOption?.label ?? 'Select an item',
      value: placeholderOption?.value ?? '',
      key: 'placeholder',
    };
  }, [placeholderOption?.label, placeholderOption?.value]);

  const [value, setValue] = useState(prefilledValue ?? placeholder.value);

  const [internalErrorText, setInternalErrorText] = useState<string | undefined>();
  const [internalValidationState, setInternalValidationState] = useState<
    'unknown' | 'valid' | 'invalid'
  >('unknown');

  const validate = useCallback(
    (value: string) => {
      let errorText: string | undefined;
      let validationState: 'valid' | 'invalid' = 'valid';
      if (value === '' && required) {
        errorText = customErrorMessages?.fieldRequired ?? 'This field is required';
        validationState = 'invalid';
      }
      setInternalErrorText(errorText);
      setInternalValidationState(validationState);
      return validationState;
    },
    [required, customErrorMessages?.fieldRequired],
  );

  const handleValueChange = useCallback(
    (val: unknown) => {
      const value = typeof val === 'string' ? val : '';
      setValue(value);
      if (onValueChange) {
        onValueChange(value);
      }
      const validationState = validate(value);
      if (onValidationChange) {
        onValidationChange(validationState);
      }
    },
    [validate, onValueChange, onValidationChange],
  );

  const onBlur = useCallback<NonNullable<typeof externalOnBlur>>(
    (e) => {
      validate(value);
      externalOnBlur && externalOnBlur(e);
    },
    [value, validate, externalOnBlur],
  );

  const errorText = externalErrorText ?? internalErrorText;
  const invalid = !!errorText || externalInvalid || internalValidationState === 'invalid';

  const { state, handleBlur, handleFocus, setFocused } = useInputState({
    errorText,
    invalid,
    disabled,
    onFocus,
    onBlur,
  });

  const handleOpen: typeof onOpen = useCallback(() => {
    setFocused(true);
    onOpen && onOpen();
  }, [setFocused, onOpen]);

  const handleClose: typeof onClose = useCallback(() => {
    setFocused(false);
    validate(value);
    onClose && onClose();
  }, [setFocused, validate, value, onClose]);

  const {
    frameHeight,
    inputStyles,
    viewContainerStyles,
    placeholderStyles,
    iconContainerStyles,
    iconStyles,
  } = useMemo(() => getStyles({ size, state }), [size, state]);

  return (
    <TrackableContainer
      analyticsName={analyticsName}
      trackImpression={trackImpression}
      eventName="inputViewEvent"
      gap={textInputTokens.padding.gap.textAndField[size]}
    >
      <InputLabel
        size={size}
        title={title}
        supportText={supportText}
        id={pickerId}
        required={required}
        state={state}
      />

      <InputFrame size={size} state={state} height={frameHeight}>
        <PickerSelect
          {...props}
          items={items}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onValueChange={handleValueChange}
          pickerProps={{
            id: pickerId,
            onFocus: handleFocus,
            onBlur: handleBlur,
            ...(isWeb
              ? {
                  'aria-invalid': state === 'error',
                  'aria-required': required,
                  name,
                }
              : {}),
          }}
          onOpen={handleOpen}
          onClose={handleClose}
          useNativeAndroidPickerStyle={false}
          fixAndroidTouchableBug={true}
          style={{
            viewContainer: viewContainerStyles,
            inputWeb: {
              ...inputStyles,
              ...(value === placeholder.value ? placeholderStyles : {}),
              outlineStyle: 'none',
              cursor: state === 'inactive' ? 'default' : 'pointer',
              appearance: 'none',
              textOverflow: 'ellipsis',
            } as StyleProp<TextStyle>,
            inputIOS: inputStyles,
            chevronUp: { display: 'none' },
            chevronDown: { display: 'none' },
            inputAndroid: inputStyles,
            headlessAndroidContainer: StyleSheet.absoluteFill,
            iconContainer: iconContainerStyles,
            placeholder: placeholderStyles,
          }}
          touchableWrapperProps={isAndroid ? { style: viewContainerStyles } : {}}
          Icon={() =>
            // This toggles the chevron icon on ios/android. On web, there doesn't
            // seem to be a good way to know if the picker is open because it's
            // possible to focus the picker without opening it and `select`
            // doesn't have an event corresponding to open/close.
            !isWeb && state === 'focused' ? (
              <ChevronUp {...iconStyles} />
            ) : (
              <ChevronDown {...iconStyles} />
            )
          }
        />
      </InputFrame>

      <InputError errorText={errorText} />
    </TrackableContainer>
  );
};

const getStyles = ({ size, state }: Pick<InputProps, 'size'> & InputStateProp) => {
  const typopgraphyToken = textInputTokens.typography.valueText[size];
  const colorToken = isKeyOfObject(state, textInputTokens.color.content.valueText.state)
    ? textInputTokens.color.content.valueText.state[state]
    : textInputTokens.color.content.valueText.default;

  const paddingVerticalToken = textInputTokens.padding.topBottom[size];
  const paddingHorizontalToken = textInputTokens.padding.leftRight[size];
  const borderWidthToken = textInputTokens.borderWidth.default;

  const paddingVertical = getTokenValue(paddingVerticalToken, 'space');
  const paddingHorizontal = getTokenValue(paddingHorizontalToken, 'space');
  const borderWidth = getTokenValue(borderWidthToken, 'space');

  const lineHeight = getTypographyValue(typopgraphyToken, 'lineHeight');
  const fontScale = PixelRatio.getFontScale();
  const frameHeight = lineHeight * fontScale + 2 * paddingVertical + 2 * borderWidth;

  const viewContainerStyles: StyleProp<ViewStyle> = {
    position: 'absolute',
    top: -borderWidth,
    bottom: -borderWidth,
    left: -borderWidth,
    right: -borderWidth,
    zIndex: 1,
  };

  const iconContainerStyles: StyleProp<ViewStyle> = {
    top: 0,
    bottom: 0,
    right: paddingHorizontal + borderWidth,
    justifyContent: 'center',
    zIndex: -1,
  };

  const fontWeight = getTypographyValue(typopgraphyToken, 'weight');
  const iconSize: 20 | 24 = size === 'small' || size === 'xsmall' ? 20 : 24;
  const iconGap = getTokenValue(textInputTokens.padding.gap.textAndIcon.default, 'space');

  const inputStyles = {
    fontFamily: getFontName(fontWeight),
    fontSize: getTypographyValue(typopgraphyToken, 'size'),
    fontWeight,
    letterSpacing: getTypographyValue(typopgraphyToken, 'letterSpacing'),
    color: getTokenValue(colorToken, 'color'),
    borderWidth,
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    height: frameHeight,
    paddingVertical,
    paddingLeft: paddingHorizontal,
    paddingRight: paddingHorizontal + iconSize + iconGap,
  };

  const placeholderStyles = {
    color: getTokenValue(textInputTokens.color.content.placeholderText.default, 'color'),
  };

  const iconStyles = {
    size: iconSize,
    color: colorToken,
  };

  return {
    frameHeight,
    viewContainerStyles,
    iconContainerStyles,
    inputStyles,
    placeholderStyles,
    iconStyles,
  };
};
